import { Redirect, useLocalSearchParams } from 'expo-router';
import { useAtomValue } from 'jotai';

import { isOwnProfileId } from '@/components/profile/ownProfileEditRedirect';
import UserProfile from '@/components/userProfile';
import { ROUTES } from '@/constants/routes';
import { userIdAtom } from '@/services/globalState';

/**
 * Someone else's profile, opened from the Directory, a chat or a link.
 *
 * Your own profile lives in Account, so `/profile/me` and `/profile/<your id>` redirect there.
 * Opened from Account, its Back returns to Account on web and native alike; opened here, it would
 * belong to whichever tab was showing.
 *
 * Every other parameter is carried across, which is not incidental: a notification deep-links to
 * a section of the profile, and a redirect that kept only the id would land the reader at the top
 * of the page having silently dropped the part that said where to go.
 *
 * Rendering nothing while the id is unknown is deliberate. This route is inside the authenticated
 * group, which redirects an unauthenticated viewer out on its own, so the id arrives moments
 * later. Redirecting before it does would send the viewer to a profile for nobody.
 */
export default function ProfileRoute() {
  const { id, ...rest } = useLocalSearchParams<{ id: string }>();

  const currentUserId = useAtomValue(userIdAtom);

  if (id === 'me' && !currentUserId) {
    return null;
  }

  if (isOwnProfileId(id, currentUserId)) {
    return <Redirect href={{ pathname: ROUTES.ACCOUNT_PROFILE, params: rest }} />;
  }

  return <UserProfile />;
}
